import { definedMap } from "@glideapps/ts-necessities";
import { CalendarListOrder } from "common/components/calendar-list-order";
import {
    asArrayValue,
    asBoolean,
    asDate,
    asString,
    isLoadedDefined,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import type { Context } from "common/components/types";
import { ActionSource, LOADING } from "common/components/types";
import type { CalendarListUI, GlideFunctionalRenderer } from "common/components/uis";
import { getNowTimeZoneAgnostic, makeStartOfDayTimeZoneAgnostic } from "date-time/support";
import { CalendarListMode } from "generator/components/calendar-list-mode";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import type { CalendarEvent } from "webapp/ui/views/calendar-schedule/calendar-schedule";
import type { CalendarEventList } from "../ui/smart/calendar-list/calendar-list";
import CalendarList from "../ui/smart/calendar-list/calendar-list";

// function makeDefaultEndDate(start: Date): Date {
//     return new Date(new Date(start).setHours(start.getHours() + 1));
// }

export const CalendarListMaterialRenderer: GlideFunctionalRenderer<CalendarListUI> = p => {
    const { env, getValueProducers, ctx, ui } = p;
    const [force] = useForce();
    const [[list, emptyMessage, caption, defaultModeValue, orderValue, onTap, insideInlineList, showTime]] =
        useForceValueProducers(getValueProducers, force, ctx);
    const { onClickWithCustomContext: onClick } = useOnClickFromAction(onTap, ActionSource.CalendarListItem, ctx, env);
    if (list === LOADING) {
        return env.renderProgress();
    }
    const isInsideInlineList = loadedDefinedMap(insideInlineList, asBoolean) ?? true;
    const startOfToday = makeStartOfDayTimeZoneAgnostic(getNowTimeZoneAgnostic());
    const eventList: CalendarEventList<Context> = [];
    for (const groupVP of asArrayValue(list)) {
        const groupInspector = groupVP.getObjectInspector(force);
        if (!isLoadedDefined(groupInspector)) continue;
        const groupName = loadedDefinedMapWithDefault(
            definedMap(groupInspector.getProperty("groupName"), force),
            "Sometime",
            asString
        );
        const events: CalendarEvent<Context>[] = [];
        for (const itemVP of loadedDefinedMapWithDefault(
            definedMap(groupInspector.getProperty("items"), force),
            [],
            asArrayValue
        )) {
            const itemInspector = itemVP.getObjectInspector(force);
            if (!isLoadedDefined(itemInspector)) continue;
            const start = loadedDefinedMap(definedMap(itemInspector.getProperty("start"), force), asDate);
            if (start === undefined) continue;
            const end = loadedDefinedMap(definedMap(itemInspector.getProperty("end"), force), asDate);
            const title = loadedDefinedMapWithDefault(
                definedMap(itemInspector.getProperty("title"), force),
                "Event",
                asString
            );
            const description = loadedDefinedMap(definedMap(itemInspector.getProperty("description"), force), asString);
            const itemID = loadedDefinedMap(definedMap(itemInspector.getProperty("itemID"), force), asString);
            const data = itemInspector.getProperty("item");
            if (!isLoadedDefined(data)) continue;
            events.push({ name: title, description, start, end: end ?? start, data, itemID });
        }
        if (events.length === 0) continue;
        const someGroupDate = events[0].start;
        const important = makeStartOfDayTimeZoneAgnostic(someGroupDate).getTime() === startOfToday.getTime();
        eventList.push({ name: groupName, important, events });
    }

    const emptyMessageValue = loadedDefinedMap(emptyMessage, asString);
    const captionValue = loadedDefinedMap(caption, asString);
    const defaultMode =
        loadedDefinedMap(defaultModeValue, v => asString(v) as CalendarListMode) ?? CalendarListMode.List;
    const order = loadedDefinedMap(orderValue, v => asString(v) as CalendarListOrder) ?? CalendarListOrder.OldestFirst;

    const saveState = (state: any) => {
        ui.state = state;
    };

    return (
        <CalendarList
            title={captionValue}
            eventList={eventList}
            emptyMessage={emptyMessageValue}
            onTap={onClick !== undefined ? e => onClick(e.data, undefined) : undefined}
            saveState={saveState}
            savedState={ui.state}
            allowSwitch={!isInsideInlineList}
            defaultMode={defaultMode}
            order={order}
            showTime={definedMap(showTime, asBoolean) ?? true}
            appKind={env.appEnvironment.appKind}
        />
    );
};
